import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { gastronomiaMenu, MenuItem } from '../../shared/data/gastronomiaData';
import { UtensilsCrossed, ShoppingBag, Plus, Minus, Flame, Clock, MapPin, ChevronRight, X, Star, Bike, CheckCircle2, Search, Percent, Tag, ArrowRight, Sparkles, Filter, Store } from 'lucide-react';

interface GastronomyDemoProps {
  prospect: Prospect;
}

export const GastronomyDemo: React.FC<GastronomyDemoProps> = ({ prospect }) => {
  const [activeCategory, setActiveCategory] = useState<'todos' | 'hamburgueres' | 'porcoes' | 'bebidas' | 'sobremesas'>('todos');
  const [deliveryType, setDeliveryType] = useState<'entrega' | 'retirada'>('entrega');
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [doneness, setDoneness] = useState<'Ao Ponto' | 'Mal Passada' | 'Bem Passada'>('Ao Ponto');
  const [selectedAddons, setSelectedAddons] = useState<string[]>(['Bacon Crocante']);
  const [cart, setCart] = useState<{ item: MenuItem; quantity: number; options: string[] }[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [coupon, setCoupon] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const root = document.documentElement;
    root.style.setProperty('--ym-prospect-primary', '#E11D48');
    root.style.setProperty('--ym-prospect-accent', '#F59E0B');
  }, []);

  const toggleAddon = (addon: string) => {
    setSelectedAddons(prev => prev.includes(addon) ? prev.filter(a => a !== addon) : [...prev, addon]);
  };

  const filteredMenu = activeCategory === 'todos' 
    ? gastronomiaMenu 
    : gastronomiaMenu.filter(m => m.category === activeCategory);

  const addToCart = (product: MenuItem) => {
    const opts = [`Ponto: ${doneness}`, ...selectedAddons];
    setCart(prev => {
      const existing = prev.find(c => c.item.id === product.id);
      if (existing) {
        return prev.map(c => c.item.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, { item: product, quantity: 1, options: opts }];
    });
    setSelectedProduct(null);
    setIsCartOpen(true);
  };

  const applyPromoCoupon = () => {
    if (coupon.toUpperCase() === 'FIRST10' || coupon.toUpperCase() === 'IFOOD10') {
      setAppliedDiscount(10);
    } else {
      setAppliedDiscount(5);
    }
  };

  const totalItems = cart.reduce((acc, c) => acc + c.quantity, 0);
  const rawTotalPrice = cart.reduce((acc, c) => acc + (c.item.price * c.quantity), 0);
  const totalPrice = Math.max(0, rawTotalPrice - appliedDiscount);
  const freeShippingThreshold = 60;
  const missingForFreeShipping = Math.max(0, freeShippingThreshold - rawTotalPrice);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-sans selection:bg-[#E11D48] selection:text-white pb-32">
      
      {/* Top Banner Promocional de Boas-Vindas */}
      <div className="bg-[#E11D48] text-white text-[11px] font-black uppercase tracking-widest py-2.5 px-6 text-center flex items-center justify-center gap-2">
         <Sparkles className="w-4 h-4 fill-amber-300 text-amber-300" />
         <span>Cupom de Boas-Vindas: Use <strong className="underline underline-offset-2">FIRST10</strong> para R$ 10 OFF no seu 1º Pedido!</span>
      </div>

      {/* Header iFood / AmoOfertas Clean White - STRICT NO SHADOWS */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
          
          <div className="flex items-center gap-4">
             <div className="w-11 h-11 rounded-2xl bg-[#E11D48] text-white flex items-center justify-center font-black text-xl border border-rose-500">
                <UtensilsCrossed className="w-6 h-6" />
             </div>
             <div>
                <h1 className="text-xl font-heading font-black text-[#0F172A] tracking-tight uppercase leading-none">{prospect.trade_name}</h1>
                <span className="text-[10px] text-[#E11D48] font-bold tracking-wider uppercase flex items-center gap-1 mt-0.5">
                  <Flame className="w-3.5 h-3.5 fill-[#E11D48]" /> Cardápio Próprio Sem Taxa
                </span>
             </div>
          </div>

          {/* Toggle Entrega / Retirada & Endereço */}
          <div className="hidden lg:flex items-center gap-4 bg-slate-100 p-1.5 rounded-2xl border border-slate-200">
             <button 
                onClick={() => setDeliveryType('entrega')}
                className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${deliveryType === 'entrega' ? 'bg-white text-[#E11D48] border border-slate-200' : 'text-slate-600 hover:text-[#0F172A]'}`}
             >
                <Bike className="w-4 h-4" /> Entrega (30-40m)
             </button>
             <button 
                onClick={() => setDeliveryType('retirada')}
                className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${deliveryType === 'retirada' ? 'bg-white text-[#E11D48] border border-slate-200' : 'text-slate-600 hover:text-[#0F172A]'}`}
             >
                <Store className="w-4 h-4" /> Retirada (15m)
             </button>
          </div>

          <div className="flex items-center gap-4">
             <div className="hidden md:flex items-center gap-2 text-xs font-bold text-slate-600 bg-slate-100 px-3.5 py-2 rounded-xl border border-slate-200">
                <MapPin className="w-4 h-4 text-[#E11D48]" />
                <span>{prospect.city} - SC</span>
             </div>

             <button 
                onClick={() => setIsCartOpen(true)}
                className="relative bg-[#E11D48] text-white px-5 py-2.5 rounded-2xl font-bold text-xs uppercase tracking-wider hover:bg-rose-700 transition-colors flex items-center gap-2 border border-rose-600"
             >
                <ShoppingBag className="w-4 h-4" />
                <span>Sacola</span>
                {totalItems > 0 && (
                   <span className="bg-white text-[#E11D48] w-5 h-5 rounded-full text-xs font-black flex items-center justify-center ml-1">
                      {totalItems}
                   </span>
                )}
             </button>
          </div>

        </div>
      </header>

      {/* Carrossel de Banners Promocionais estilo iFood / AmoOfertas - STRICT NO SHADOWS */}
      <section className="max-w-7xl mx-auto px-6 pt-8 pb-4">
         <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Banner 1 */}
            <div className="bg-gradient-to-r from-rose-600 to-[#E11D48] text-white p-6 rounded-2xl border border-rose-500 flex justify-between items-center relative overflow-hidden">
               <div className="space-y-2 z-10">
                  <span className="bg-white/20 text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-md">Semana do Consumidor</span>
                  <h3 className="text-2xl font-heading font-black">20% OFF nos Combos</h3>
                  <p className="text-xs text-rose-100 font-light">Peça o Combo Smash + Fritas por R$ 39,90</p>
               </div>
               <Percent className="w-24 h-24 text-white/10 absolute -right-4 -bottom-4" />
            </div>

            {/* Banner 2 */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6 rounded-2xl border border-amber-400 flex justify-between items-center relative overflow-hidden">
               <div className="space-y-2 z-10">
                  <span className="bg-white/20 text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-md">Frete Grátis</span>
                  <h3 className="text-2xl font-heading font-black">Entregas Acima R$ 60</h3>
                  <p className="text-xs text-amber-100 font-light">Válido para todos os bairros de {prospect.city}</p>
               </div>
               <Bike className="w-24 h-24 text-white/10 absolute -right-4 -bottom-4" />
            </div>

            {/* Banner 3 */}
            <div className="bg-gradient-to-r from-slate-900 to-[#0F172A] text-white p-6 rounded-2xl border border-slate-800 flex justify-between items-center relative overflow-hidden">
               <div className="space-y-2 z-10">
                  <span className="bg-amber-400 text-black text-[10px] font-black uppercase px-2.5 py-1 rounded-md">Clube Fidelidade</span>
                  <h3 className="text-2xl font-heading font-black">Ganhe 5% Cashback</h3>
                  <p className="text-xs text-slate-300 font-light">Saldo automático no seu WhatsApp</p>
               </div>
               <Tag className="w-24 h-24 text-white/10 absolute -right-4 -bottom-4" />
            </div>

         </div>
      </section>

      {/* Pílulas de Categoria com Ícones - STRICT NO SHADOWS */}
      <section className="sticky top-20 z-40 bg-white/95 backdrop-blur-md border-y border-slate-200 py-4 px-6 mt-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
           <div className="flex gap-3 overflow-x-auto ym-hide-scrollbar">
              {[
                 { id: 'todos', label: '🔥 Todos os Pratos' },
                 { id: 'hamburgueres', label: '🍔 Burgers Artesanais' },
                 { id: 'porcoes', label: '🍟 Porções & Acompanhamentos' },
                 { id: 'bebidas', label: '🍺 Cervejas & Bebidas' },
                 { id: 'sobremesas', label: '🍰 Sobremesas' },
              ].map(cat => (
                 <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id as any)}
                    className={`px-5 py-2.5 rounded-2xl text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-colors border ${
                       activeCategory === cat.id 
                          ? 'bg-[#E11D48] text-white border-rose-600' 
                          : 'bg-slate-100 text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-200'
                    }`}
                 >
                    {cat.label}
                 </button>
              ))}
           </div>
           
           <div className="hidden sm:flex items-center gap-2 text-xs font-bold text-slate-500">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              <span>4.9 (620+ avaliações no iFood)</span>
           </div>
        </div>
      </section>

      {/* Grid do Cardápio Clean White - STRICT NO SHADOWS */}
      <section className="max-w-7xl mx-auto px-6 pt-8">
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredMenu.map(item => (
               <div 
                  key={item.id} 
                  onClick={() => setSelectedProduct(item)}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-[#E11D48] transition-colors cursor-pointer group flex flex-col justify-between p-4"
               >
                  <div>
                     <div className="relative aspect-[16/10] rounded-xl overflow-hidden mb-4 border border-slate-100">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        {item.badge && (
                           <div className="absolute top-3 left-3 bg-[#E11D48] text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-md border border-rose-500">
                              {item.badge}
                           </div>
                        )}
                        <div className="absolute bottom-3 right-3 bg-white/95 text-[#0F172A] text-[10px] font-bold px-2.5 py-1 rounded-md border border-slate-200 flex items-center gap-1">
                           <Clock className="w-3 h-3 text-[#E11D48]" /> 12-15m na chapa
                        </div>
                     </div>
                     
                     <h3 className="font-heading font-black text-xl text-[#0F172A] mb-1 group-hover:text-[#E11D48] transition-colors">{item.name}</h3>
                     <p className="text-xs text-slate-500 font-light leading-relaxed mb-4 line-clamp-2">{item.description}</p>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                     <div>
                        <span className="text-xl font-black text-[#0F172A]">R$ {item.price.toFixed(2)}</span>
                        {item.price > 40 && <span className="text-[10px] text-emerald-600 font-bold block">✓ Frete Grátis com Cupom</span>}
                     </div>
                     <button className="bg-[#E11D48] hover:bg-rose-700 text-white p-2.5 rounded-xl transition-colors border border-rose-600 flex items-center gap-1.5 text-xs font-bold">
                        <Plus className="w-4 h-4" /> Monte o Seu
                     </button>
                  </div>
               </div>
            ))}
         </div>
      </section>

      {/* Modal de Personalização e Montagem do Hambúrguer - STRICT NO SHADOWS */}
      {selectedProduct && (
         <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white border border-slate-300 rounded-2xl max-w-lg w-full overflow-hidden">
               <div className="relative h-52 border-b border-slate-200">
                  <img src={selectedProduct.image} className="w-full h-full object-cover" alt={selectedProduct.name} />
                  <button onClick={() => setSelectedProduct(null)} className="absolute top-4 right-4 w-9 h-9 rounded-xl bg-white/90 text-[#0F172A] flex items-center justify-center border border-slate-300 font-bold">
                     <X className="w-5 h-5" />
                  </button>
               </div>

               <div className="p-6">
                  <h3 className="text-2xl font-black font-heading text-[#0F172A] mb-1">{selectedProduct.name}</h3>
                  <p className="text-xs text-slate-500 mb-6">{selectedProduct.description}</p>
                  
                  {/* Escolha do Ponto da Carne */}
                  <div className="mb-6">
                     <h4 className="text-xs font-bold uppercase tracking-wider text-[#E11D48] mb-3 flex items-center justify-between">
                        <span>Ponto da Carne (Obrigatório)</span>
                        <span className="text-[10px] bg-rose-50 text-[#E11D48] px-2 py-0.5 rounded border border-rose-200">Selecione 1</span>
                     </h4>
                     <div className="grid grid-cols-3 gap-2">
                        {(['Ao Ponto', 'Mal Passada', 'Bem Passada'] as const).map((p) => (
                           <button
                              key={p}
                              onClick={() => setDoneness(p)}
                              className={`p-3 rounded-xl border text-xs font-bold transition-colors ${doneness === p ? 'bg-[#E11D48] text-white border-rose-600' : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300'}`}
                           >
                              {p}
                           </button>
                        ))}
                     </div>
                  </div>

                  {/* Adicionais Extras */}
                  <div className="mb-6">
                     <h4 className="text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-3">Adicionais Extras</h4>
                     <div className="space-y-2">
                        {[
                           { name: 'Bacon Crocante Em Fatias', price: 6.00 },
                           { name: 'Queijo Cheddar Inglês Duplo', price: 7.50 },
                           { name: 'Molho Especial da Casa (Pote 50ml)', price: 4.00 },
                        ].map((add) => (
                           <label 
                              key={add.name}
                              onClick={() => toggleAddon(add.name)}
                              className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer text-xs font-semibold ${selectedAddons.includes(add.name) ? 'bg-rose-50 border-[#E11D48] text-[#0F172A]' : 'bg-slate-50 border-slate-200 text-slate-700'}`}
                           >
                              <span>{add.name} (+ R$ {add.price.toFixed(2)})</span>
                              <input type="checkbox" checked={selectedAddons.includes(add.name)} readOnly className="accent-[#E11D48]" />
                           </label>
                        ))}
                     </div>
                  </div>

                  <button 
                     onClick={() => addToCart(selectedProduct)}
                     className="w-full bg-[#E11D48] text-white py-4 rounded-xl font-black text-xs uppercase tracking-wider hover:bg-rose-700 transition-colors flex items-center justify-between px-6 border border-rose-600"
                  >
                     <span>Adicionar à Sacola</span>
                     <span>R$ {selectedProduct.price.toFixed(2)}</span>
                  </button>
               </div>
            </div>
         </div>
      )}

      {/* Cart Drawer Flutuante Clean White - STRICT NO SHADOWS */}
      {isCartOpen && (
         <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end">
            <div className="bg-white w-full max-w-md h-full p-6 flex flex-col justify-between border-l border-slate-300">
               <div>
                  <div className="flex justify-between items-center pb-6 border-b border-slate-200 mb-6">
                     <h3 className="font-heading font-black text-lg uppercase tracking-wider flex items-center gap-2 text-[#0F172A]">
                        <ShoppingBag className="w-5 h-5 text-[#E11D48]" /> Sua Sacola de Pedidos
                     </h3>
                     <button onClick={() => setIsCartOpen(false)} className="text-slate-400 hover:text-[#0F172A]"><X className="w-6 h-6" /></button>
                  </div>

                  {/* Frete Grátis Progressivo */}
                  <div className="bg-slate-50 p-4 rounded-xl mb-6 border border-slate-200">
                     {missingForFreeShipping > 0 ? (
                        <p className="text-xs font-bold text-slate-700">
                           Faltam <span className="text-[#E11D48]">R$ {missingForFreeShipping.toFixed(2)}</span> para FRETE GRÁTIS!
                        </p>
                     ) : (
                        <p className="text-xs font-bold text-emerald-600 flex items-center gap-1.5">
                           <CheckCircle2 className="w-4 h-4" /> Parabéns! Você ganhou FRETE GRÁTIS!
                        </p>
                     )}
                     <div className="w-full h-2 bg-slate-200 rounded-full mt-2 overflow-hidden">
                        <div className="h-full bg-[#E11D48]" style={{ width: `${Math.min(100, (rawTotalPrice / freeShippingThreshold) * 100)}%` }}></div>
                     </div>
                  </div>

                  {cart.length === 0 ? (
                     <p className="text-center text-slate-400 py-12 text-sm font-light">Sua sacola está vazia.</p>
                  ) : (
                     <div className="space-y-4 max-h-[45vh] overflow-y-auto pr-2">
                        {cart.map((c, i) => (
                           <div key={i} className="flex items-center justify-between bg-slate-50 p-4 rounded-xl border border-slate-200">
                              <div className="flex items-center gap-3">
                                 <img src={c.item.image} className="w-12 h-12 object-cover rounded-lg border border-slate-200" alt={c.item.name} />
                                 <div>
                                    <h4 className="font-bold text-xs text-[#0F172A] leading-tight">{c.item.name}</h4>
                                    <p className="text-[10px] text-slate-500">{c.options.join(', ')}</p>
                                    <p className="text-xs font-black text-[#E11D48] mt-1">R$ {(c.item.price * c.quantity).toFixed(2)}</p>
                                 </div>
                              </div>
                              <div className="flex items-center gap-2 bg-white px-2 py-1 rounded-lg border border-slate-200">
                                 <button onClick={() => setCart(prev => prev.map((item, idx) => idx === i ? { ...item, quantity: Math.max(1, item.quantity - 1) } : item))}><Minus className="w-3 h-3 text-slate-500" /></button>
                                 <span className="text-xs font-bold">{c.quantity}</span>
                                 <button onClick={() => setCart(prev => prev.map((item, idx) => idx === i ? { ...item, quantity: item.quantity + 1 } : item))}><Plus className="w-3 h-3 text-slate-500" /></button>
                              </div>
                           </div>
                        ))}
                     </div>
                  )}

                  {/* Cupom de Desconto */}
                  {cart.length > 0 && (
                     <div className="mt-6 flex gap-2">
                        <input 
                           type="text" 
                           placeholder="Digite o cupom (ex: FIRST10)" 
                           value={coupon}
                           onChange={e => setCoupon(e.target.value)}
                           className="flex-1 px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs outline-none uppercase font-bold text-[#0F172A]" 
                        />
                        <button onClick={applyPromoCoupon} className="px-4 py-2 bg-slate-900 text-white rounded-xl font-bold text-xs uppercase">Aplicar</button>
                     </div>
                  )}
               </div>

               {cart.length > 0 && (
                  <div className="pt-6 border-t border-slate-200">
                     {appliedDiscount > 0 && (
                        <div className="flex justify-between items-center text-xs font-bold text-emerald-600 mb-2">
                           <span>Desconto do Cupom:</span>
                           <span>- R$ {appliedDiscount.toFixed(2)}</span>
                        </div>
                     )}
                     <div className="flex justify-between items-center mb-4">
                        <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Total do Pedido:</span>
                        <span className="text-2xl font-black text-[#0F172A]">R$ {totalPrice.toFixed(2)}</span>
                     </div>
                     <button className="w-full bg-[#E11D48] text-white py-4 rounded-xl font-black uppercase tracking-wider text-xs hover:bg-rose-700 transition-colors border border-rose-600">
                        Finalizar Pedido sem Comissões
                     </button>
                  </div>
               )}
            </div>
         </div>
      )}

    </div>
  );
};
